import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';

import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { isFieldRequiredForNextStatusesInFillInProcess } from 'KnmErknmPackage/MainModule/common/fieldsRequiredForNextStatusesInFillInProcess';
import { ApproveRequiredTypesEnum } from 'APIPackage/KnmModule/enums/ApproveRequiredTypesEnum';
import { isFieldRequiredForNextStatusesInTypeCarriedOut } from 'KnmErknmPackage/MainModule/common/fieldsRequiredForNextStatusesInTypeCarriedOut';
import { KNM_APPROVE_INFO } from 'CorePackage/MainModule/routes/routes';
import { getIsPlanErknmStatusApproved } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getKnoSupervisionStartAvailableUnplannedActivities } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { isFieldRequiredForNextStatusesInReadyForApprove } from 'KnmErknmPackage/MainModule/common/fieldsRequiredForNextStatusesInReadyForApprove';
import { isFieldRequiredForNextStatusesInTypeWaitingCarriedOut } from 'KnmErknmPackage/MainModule/common/fieldsRequiredForNextStatusesInTypeWaitingCarriedOut';
import { erknmFieldsMarkedAsRequired } from 'KnmErknmPackage/MainModule/common/erknmFieldsMarkedAsRequired';
import { getLatestKnmErknmChangePlanReason } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';

import { IKnmFormRenderProps } from '../components/KnmForm';
import { IKnmForm } from '../interfaces/IKnmForm';
import {
  getKnmData,
  getKnmFormRef,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejectAppealed,
  getKnmIsErknmStatusRejected,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsErpStatusTypeCarriedOut,
  getKnmSystemType,
} from '../selectors/knmSelectors';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { IErknmStatusHintField } from '../interfaces/IErknmStatusHintField';
import { getErrorFieldDataByFlatName } from '../common/getErrorFieldDataByFlatName';
import { knmSetStatusHintFields, knmSetStatusHintText } from '../actions/knmActions';

interface IUseKnmFormStatusHintInitParams {
  values: IKnmForm;
  touched: IKnmFormRenderProps['touched'];
  settings: IAdminSetting[] | undefined;
}

const getEmptyMarkedFields = (
  markedFields: string[],
  formRef: IKnmFormRef | undefined,
  knmSystemType: KnmSystemTypesEnum | undefined,
  created: string | null | undefined,
  settings: IAdminSetting[] | undefined,
): IErknmStatusHintField[] => {
  const result: IErknmStatusHintField[] = [];
  const seenTitles = new Set<string>();
  const emptyMarkedFields = markedFields.filter(item => {
    const value = formRef?.current.getFieldState(item as keyof IKnmForm)?.value;

    return !value || (Array.isArray(value) && !value.length);
  });

  emptyMarkedFields.forEach(item => {
    const fieldData =
      formRef && knmSystemType
        ? getErrorFieldDataByFlatName(item, formRef, knmSystemType, created, settings)
        : undefined;

    // для избежания дубликатов из touched и шаблона
    if (!fieldData?.title || seenTitles.has(fieldData.title)) {
      return;
    }

    seenTitles.add(fieldData.title);
    result.push({ scrollToId: fieldData.scrollElementId, title: fieldData.title });
  });

  return result;
};

export function useKnmFormStatusHintInit({ values, touched, settings }: IUseKnmFormStatusHintInitParams) {
  const dispatch = useDispatch();
  const isSupervisor = useSelector(getUserRoleIsSupervisor);
  const isProsecutorOrSuperProsecutor = useSelector(getUserRoleIsProsecutorOrSuperProsecutor);
  const isKnmStatusFillInProcess = useSelector(getKnmIsErknmStatusFillInProcess);
  const isKnmStatusTypeCarriedOut = useSelector(getKnmIsErpStatusTypeCarriedOut);
  const isKnmStatusApproving = useSelector(getKnmIsErknmStatusApproving);
  const isKnmStatusCommentsExist = useSelector(getKnmIsErknmStatusCommentsExist);
  const isKnmStatusRejected = useSelector(getKnmIsErknmStatusRejected);
  const isKnmStatusRejectAppealed = useSelector(getKnmIsErknmStatusRejectAppealed);
  const isKnmStatusReadyForApprove = useSelector(getKnmIsErknmStatusReadyForApprove);
  const isKnmStatusTypeWaitingCarryOut = useSelector(getKnmIsErknmStatusTypeWaitingCarryOut);
  const knmSystemType = useSelector(getKnmSystemType);
  const knmData = useSelector(getKnmData);
  const formRef = useSelector(getKnmFormRef);
  const knmPlanStatus = useSelector(getPlanListPlanErknmStatus);
  const isAvailableUnplannedActivities = useSelector(getKnoSupervisionStartAvailableUnplannedActivities);
  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    values.created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );
  const isRsnChangePpXxii =
    useSelector(getLatestKnmErknmChangePlanReason)?.reasonChangeType?.code === 'RSN_CHNG_PP_XXII';

  const updateFillInProcessStatusHint = useCallback(() => {
    const touchedMarkedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldRequiredForNextStatusesInFillInProcess(item, values, !!isAvailableUnplannedActivities),
        )
      : [];

    const requiredMarkedFields = erknmFieldsMarkedAsRequired.filter(item =>
      isFieldRequiredForNextStatusesInFillInProcess(item, values, !!isAvailableUnplannedActivities),
    );

    const markedFields = Array.from(new Set([...touchedMarkedFields, ...requiredMarkedFields]));

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    const approveRequired = knmData?.knmErknm?.approve?.approveRequired;
    const nextStatusTitle =
      approveRequired === ApproveRequiredTypesEnum.REQUIRED ? 'Готово к согласованию' : 'Ожидает проведения';
    const text = emptyMarkedFields.length
      ? `Для перехода в статус “${nextStatusTitle}” необходимо заполнить указанные сведения и подписать паспорт:`
      : `Для перехода в статус “${nextStatusTitle}” необходимо подписать паспорт`;

    dispatch(knmSetStatusHintText(text));
    dispatch(knmSetStatusHintFields(emptyMarkedFields));
  }, [
    touched,
    values,
    knmData,
    formRef,
    knmSystemType,
    dispatch,
    values.created,
    settings,
    isAvailableUnplannedActivities,
  ]);

  const updateTypeCarriedOutStatusHint = useCallback(() => {
    const markedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldRequiredForNextStatusesInTypeCarriedOut(item, values, isEnablePaperlessPassportDocument),
        )
      : [];
    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    if (emptyMarkedFields.length) {
      dispatch(
        knmSetStatusHintText('Для перехода паспорта в статус «Завершено» необходимо заполнить сведения об акте'),
      );
      dispatch(knmSetStatusHintFields(emptyMarkedFields));
    } else if (isEnablePaperlessPassportDocument) {
      dispatch(
        knmSetStatusHintText(
          'Для перехода паспорта в статус «Завершено» необходимо сохранить и подписать сведения об акте',
        ),
      );
      dispatch(knmSetStatusHintFields([]));
    }
  }, [touched, values, formRef, knmSystemType, dispatch, values.created, settings]);

  const updateApprovingStatusHint = useCallback(() => {
    let changePlanPrefix = '';

    if (isRsnChangePpXxii) {
      changePlanPrefix = 'Внесены изменения в разделе “Предмет КНМ”\n\n';
    }
    dispatch(
      knmSetStatusHintText(
        `${changePlanPrefix} Для перехода паспорта в следующий статус необходимо принять решение по заявлению`,
      ),
    );
    dispatch(
      knmSetStatusHintFields([
        {
          scrollToId: KNM_APPROVE_INFO,
          title: 'Сведения о согласовании проведения КНМ с органами прокуратуры',
        },
      ]),
    );
  }, [dispatch, isRsnChangePpXxii]);

  const updateCommentsExistStatusHint = useCallback(() => {
    const touchedMarkedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldRequiredForNextStatusesInFillInProcess(item, values, !!isAvailableUnplannedActivities),
        )
      : [];

    const requiredMarkedFields = erknmFieldsMarkedAsRequired.filter(item =>
      isFieldRequiredForNextStatusesInFillInProcess(item, values, !!isAvailableUnplannedActivities),
    );

    const markedFields = Array.from(new Set([...touchedMarkedFields, ...requiredMarkedFields]));

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);
    const text = emptyMarkedFields.length
      ? `Для перехода в следующий статус необходимо заполнить указанные сведения и подписать паспорт:`
      : `Необходимо устранить замечания, полученные от органа прокуратуры и подписать паспорт`;

    dispatch(knmSetStatusHintText(text));
    dispatch(knmSetStatusHintFields(emptyMarkedFields));
  }, [touched, values, formRef, knmSystemType, settings, dispatch, isAvailableUnplannedActivities]);

  const updateRejectedStatusHint = useCallback(() => {
    dispatch(
      knmSetStatusHintText(
        'Для обжалования решения необходимо заполнить поля “Решение обжаловано” и “Сведения об обжаловании” и подписать паспорт',
      ),
    );
    dispatch(knmSetStatusHintFields([]));
  }, [dispatch]);

  const updateRejectAppealedStatusHint = useCallback(() => {
    dispatch(knmSetStatusHintText('Для перехода паспорта в следующий статус необходимо принять решение по заявлению'));
    dispatch(knmSetStatusHintFields([]));
  }, [dispatch]);

  const updateReadyForApproveStatusHint = useCallback(() => {
    const isKnmPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(knmPlanStatus);
    const isWithOnApproveButton = knmData?.type?.code === KnmTypeCodesEnum.PP ? isKnmPlanErknmStatusApproved : true;
    const touchedMarkedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldRequiredForNextStatusesInReadyForApprove(item, values, !!isAvailableUnplannedActivities),
        )
      : [];

    const requiredMarkedFields = erknmFieldsMarkedAsRequired.filter(item =>
      isFieldRequiredForNextStatusesInReadyForApprove(item, values, !!isAvailableUnplannedActivities),
    );

    const markedFields = Array.from(new Set([...touchedMarkedFields, ...requiredMarkedFields]));

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    let text: string = '';

    if (isWithOnApproveButton) {
      if (emptyMarkedFields.length) {
        text = `Для перехода в статус “На согласовании” необходимо заполнить указанные сведения и подписать паспорт`;
      } else {
        text = 'Для перехода в статус “На согласовании” необходимо нажать на кнопку “Отправить на согласование”';
      }
    } else {
      text = 'Паспорт перейдет в статус “На согласовании” после отправки плана на рассмотрение';
    }

    dispatch(knmSetStatusHintText(text));
    dispatch(knmSetStatusHintFields(emptyMarkedFields));
  }, [
    dispatch,
    knmPlanStatus,
    knmData,
    values,
    touched,
    formRef,
    knmSystemType,
    settings,
    isAvailableUnplannedActivities,
  ]);

  const updateTypeWaitingCarryOutStatusHint = useCallback(() => {
    const touchedMarkedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldRequiredForNextStatusesInTypeWaitingCarriedOut(item, values, !!isAvailableUnplannedActivities),
        )
      : [];

    const requiredMarkedFields = erknmFieldsMarkedAsRequired.filter(item =>
      isFieldRequiredForNextStatusesInTypeWaitingCarriedOut(item, values, !!isAvailableUnplannedActivities),
    );

    const markedFields = Array.from(new Set([...touchedMarkedFields, ...requiredMarkedFields]));

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    dispatch(
      knmSetStatusHintText(
        values?.subjectNotifications?.length
          ? 'Паспорт перейдет в статус “Ожидает завершения” при наступлении даты, указанной в поле “Дата начала КНМ”'
          : 'Для перехода в статус “Ожидает завершения” необходимо заполнить указанные сведения',
      ),
    );
    dispatch(knmSetStatusHintFields(emptyMarkedFields));
  }, [dispatch, values, touched, formRef, knmSystemType, settings, isAvailableUnplannedActivities]);

  useEffect(() => {
    if (isSupervisor && isKnmStatusFillInProcess) {
      updateFillInProcessStatusHint();
    } else if (isSupervisor && isKnmStatusTypeCarriedOut) {
      updateTypeCarriedOutStatusHint();
    } else if (isProsecutorOrSuperProsecutor && isKnmStatusApproving) {
      updateApprovingStatusHint();
    } else if (isSupervisor && isKnmStatusCommentsExist) {
      updateCommentsExistStatusHint();
    } else if (isSupervisor && isKnmStatusRejected) {
      updateRejectedStatusHint();
    } else if (isProsecutorOrSuperProsecutor && isKnmStatusRejectAppealed) {
      updateRejectAppealedStatusHint();
    } else if (isSupervisor && isKnmStatusReadyForApprove) {
      updateReadyForApproveStatusHint();
    } else if (isSupervisor && isKnmStatusTypeWaitingCarryOut) {
      updateTypeWaitingCarryOutStatusHint();
    } else {
      dispatch(knmSetStatusHintText(undefined));
      dispatch(knmSetStatusHintFields([]));
    }
  }, [
    isSupervisor,
    isProsecutorOrSuperProsecutor,
    isKnmStatusFillInProcess,
    isKnmStatusTypeCarriedOut,
    isKnmStatusApproving,
    isKnmStatusCommentsExist,
    isKnmStatusRejected,
    isKnmStatusRejectAppealed,
    isKnmStatusReadyForApprove,
    isKnmStatusTypeWaitingCarryOut,
    dispatch,
    updateFillInProcessStatusHint,
    updateTypeCarriedOutStatusHint,
    updateApprovingStatusHint,
    updateCommentsExistStatusHint,
    updateRejectedStatusHint,
    updateRejectAppealedStatusHint,
    updateReadyForApproveStatusHint,
    updateTypeWaitingCarryOutStatusHint,
  ]);
}
